import { Link } from "@tanstack/react-router";
import { ArrowRight, FlaskConical, GitBranch, LineChart, Target } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAssetChangeAudits, useEvalRuns } from "@/lib/api/eval";
import type { AssetChangeAuditItem, EvalRunItem } from "@/lib/api/eval";

const SCENARIO_METRICS = [
	{
		scenario: "售后办理",
		goal: "用户能拿到订单、退款、退货等明确下一步",
		northStar: "办理路径正确率",
		coverage: "Golden15 订单/退款样本 + Badcase 回归集",
		judgement: "意图、方案、工具事实和转人工边界同时达标",
		next: "补足高频售后表达和退款/退货边界样本",
	},
	{
		scenario: "售前导购",
		goal: "回答商品、库存、适配和政策问题时不编造事实",
		northStar: "事实回答可信率",
		coverage: "Golden15 商品咨询样本 + 知识证据专项样本",
		judgement: "商品事实、知识证据和回复表达一致",
		next: "扩充商品问法和知识命中失败样本",
	},
	{
		scenario: "弱意图澄清",
		goal: "用户表达不完整时先澄清，不误判为强动作",
		northStar: "澄清命中率",
		coverage: "基础回归弱表达样本 + 意图分类失败样本",
		judgement: "弱意图进入澄清或安全兜底，不直接执行动作",
		next: "补充短句、口语、省略表达的分类样本",
	},
	{
		scenario: "R2 高风险动作",
		goal: "涉及地址、退款、取消等风险动作时必须保护执行",
		northStar: "保护执行率",
		coverage: "R2 写操作边界样本 + 保护性试跑 / 权限阻断样本",
		judgement: "真实写入前有权限、确认和保护性试跑证据",
		next: "等版本责任链稳定后再扩完整实验",
	},
];

const VERSION_CHAIN_STEPS = [
	{
		title: "评测运行",
		description: "先确定这次结论来自哪一次 baseline/current 运行。",
	},
	{
		title: "数据集版本",
		description: "确认样本集和版本一致，避免拿不同样本直接对比。",
	},
	{
		title: "资产版本",
		description: "看本次结果绑定了哪些规则、方案、提示词或证据资产。",
	},
	{
		title: "责任资产",
		description: "失败样本要能落到意图、方案、工具、知识或话术资产。",
	},
	{
		title: "对比快照",
		description: "把修复、回退和候选 Badcase 保存为复盘证据。",
	},
];

const EXPERIMENT_READINESS = [
	{
		item: "场景指标",
		status: "已具备基线",
		evidence: "已把售后办理、售前导购、弱意图澄清和 R2 高风险动作拆成北极星指标。",
		next: "继续扩充各场景样本，避免实验只优化少数高频问法。",
	},
	{
		item: "版本责任链",
		status: "已接入审计",
		evidence: "已把评测运行、资产版本和统一资产审计挂在同一条评测链路上。",
		next: "继续把具体资产编辑入口自动写入审计，减少人工补录。",
	},
	{
		item: "回归样本",
		status: "已有闭环 MVP",
		evidence: "Badcase 已支持复跑、写回回归集、批量回归执行和批量关闭。",
		next: "补实验样本分层，区分核心样本、风险样本和观察样本。",
	},
	{
		item: "审核队列",
		status: "后置",
		evidence: "当前只支持评测运营内的人工判断和批量关闭，不做线上候选自动放量。",
		next: "等实验结论和责任链稳定后，再建设候选发布和审核队列。",
	},
];

const EXPERIMENT_STEPS = [
	"确定业务场景和北极星指标",
	"绑定 baseline/current 运行与资产版本",
	"用一致样本做离线对比和回归风险检查",
	"沉淀实验结论、责任资产和后续动作",
];

const EXPERIMENT_CONFIG_CONTRACT = [
	{
		item: "实验对象",
		required: "明确本轮改的是提示词、处理方案、意图规则、知识、工具动作或质量策略。",
		current: "先绑定资产审计和责任资产，不做真实流量。",
		fallback: "对象说不清时，只能回到资产审计入口补变更记录。",
	},
	{
		item: "业务场景",
		required: "必须选择售后办理、售前导购、弱意图澄清或 R2 高风险动作等核心场景。",
		current: "先用场景指标解释实验目标，不用总通过率替代业务目标。",
		fallback: "场景不明确时，不进入实验结论，只补样本和目标说明。",
	},
	{
		item: "样本分层",
		required: "区分核心样本、风险样本、回归样本和观察样本，避免只看少数好样本。",
		current: "先复用 Golden15、Badcase 回归集和批量回归样本。",
		fallback: "样本不足时，先进入样本库补齐，不生成候选发布。",
	},
	{
		item: "baseline/current",
		required: "必须绑定同一数据集、同一 data_mode、同一 run_type 的前后运行。",
		current: "先复用 EvalRun compare 和对比快照，不接线上分流。",
		fallback: "数据口径不一致时，只能作为观察记录，不能作为实验胜负。",
	},
	{
		item: "准入阈值",
		required: "主指标不能下降，R2 保护类样本不能回退，新增 P1 badcase 必须为 0。",
		current: "先作为页面检查项展示，后续再沉淀为实验结论快照。",
		fallback: "未达阈值时回到资产修复或样本补齐，不进入候选发布。",
	},
	{
		item: "暂停条件",
		required: "出现高风险回退、版本责任链缺失、样本口径不一致或审核缺位时暂停。",
		current: "先提示产品/运营按暂停条件复核，不自动放量。",
		fallback: "触发暂停条件时，只保留复盘，不生成线上候选。",
	},
];

const EXPERIMENT_SNAPSHOT_STANDARDS = [
	{
		title: "可追溯",
		description: "结论必须能追到 EvalRun、数据集版本、资产审计和责任资产。",
	},
	{
		title: "可对比",
		description: "baseline/current 必须同样本、同 data_mode、同 run_type，并保存对比快照。",
	},
	{
		title: "可审核",
		description: "结论要有准入阈值、暂停条件、复核人和下一步动作，不自动放量。",
	},
	{
		title: "可验收",
		description: "产品/运营要能按场景指标、回归风险和未完成边界判断是否通过。",
	},
];

const EXPERIMENT_SNAPSHOT_FIELDS = [
	{
		item: "离线对比",
		source: "EvalRun compare",
		meaning: "记录 baseline/current 的指标差异、修复样本和新增回退样本。",
	},
	{
		item: "资产审计",
		source: "asset_change_audit_refs",
		meaning: "解释这次结论和哪些提示词、规则、方案、知识或工具改动有关。",
	},
	{
		item: "责任资产",
		source: "Badcase / EvalSampleResult",
		meaning: "把失败或回退样本落到意图、方案、工具、知识或话术资产。",
	},
	{
		item: "对比快照",
		source: "EvalRunCompareSnapshot",
		meaning: "把一次离线对比沉淀为复盘证据；本轮不生成线上候选。",
	},
];

export function ScenarioMetricsPanel() {
	return (
		<Card data-testid="m07-scenario-metrics">
			<CardHeader className="space-y-2">
				<div className="flex items-start gap-3">
					<span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
						<Target className="h-4 w-4" aria-hidden="true" />
					</span>
					<div>
						<CardTitle className="text-lg">场景指标基线</CardTitle>
						<CardDescription className="mt-1 leading-6">
							按业务场景看效果是否变好，不只看总体通过率。
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="grid gap-3 md:grid-cols-4">
					{SCENARIO_METRICS.map((item) => (
						<article
							key={item.scenario}
							className="rounded-md border border-surface-border bg-surface-background px-3 py-3"
						>
							<p className="text-sm font-semibold text-text-primary">{item.scenario}</p>
							<p className="mt-2 text-xs font-medium text-brand-primary">北极星指标</p>
							<p className="mt-1 text-sm leading-6 text-text-primary">{item.northStar}</p>
							<p className="mt-2 text-xs leading-5 text-text-secondary">{item.goal}</p>
						</article>
					))}
				</div>
				<div className="overflow-x-auto">
					<table className="w-full min-w-[900px] border-collapse text-sm">
						<thead className="bg-surface-subtle text-left text-xs text-text-muted">
							<tr>
								<th className="px-3 py-2 font-medium">场景</th>
								<th className="px-3 py-2 font-medium">测试集覆盖</th>
								<th className="px-3 py-2 font-medium">判断口径</th>
								<th className="px-3 py-2 font-medium">下一步补齐</th>
							</tr>
						</thead>
						<tbody>
							{SCENARIO_METRICS.map((item) => (
								<tr key={item.scenario} className="border-t border-surface-border align-top">
									<td className="px-3 py-3 font-medium text-text-primary">{item.scenario}</td>
									<td className="px-3 py-3 text-text-secondary">{item.coverage}</td>
									<td className="px-3 py-3 text-text-secondary">{item.judgement}</td>
									<td className="px-3 py-3 text-text-secondary">{item.next}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<div className="flex flex-col gap-2 rounded-md border border-surface-border bg-surface-background px-3 py-3 md:flex-row md:items-center md:justify-between">
					<p className="text-sm leading-6 text-text-secondary">
						先建立场景指标、样本覆盖和问题判断口径；完整线上实验和候选发布继续后置。
					</p>
					<div className="flex flex-col gap-2 sm:flex-row">
						<PanelLink to="/test-datasets" label="去样本库" />
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

export function VersionResponsibilityChainPanel() {
	const runs = useEvalRuns();
	const currentRun = findCurrentRun(runs.data?.items ?? []);
	const audits = useAssetChangeAudits();
	const auditRefs = getAuditRefs(currentRun);
	const assetEntries = Object.entries(currentRun?.asset_versions ?? {}).filter(
		([key, value]) => key !== "asset_change_audit_refs" && value !== null && value !== undefined,
	);
	const auditItems = audits.data?.items ?? [];

	return (
		<Card data-testid="m07-version-responsibility-chain">
			<CardHeader className="space-y-2">
				<div className="flex items-start gap-3">
					<span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
						<GitBranch className="h-4 w-4" aria-hidden="true" />
					</span>
					<div>
						<div className="flex flex-wrap items-center gap-2">
							<CardTitle className="text-lg">版本责任链</CardTitle>
							<span className="rounded border border-brand-success/30 bg-brand-success/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand-success">
								审计已接入
							</span>
						</div>
						<CardDescription className="mt-1 leading-6">
							把一次评测结论追到数据集、资产版本、资产变更审计、责任资产和对比快照，判断“这次变好和谁有关”。
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="grid gap-3 md:grid-cols-5">
					{VERSION_CHAIN_STEPS.map((step, index) => (
						<article
							key={step.title}
							className="rounded-md border border-surface-border bg-surface-background px-3 py-3"
						>
							<div className="flex items-center gap-2">
								<span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-primary/10 text-xs font-semibold text-brand-primary">
									{index + 1}
								</span>
								<p className="text-sm font-semibold text-text-primary">{step.title}</p>
							</div>
							<p className="mt-2 text-xs leading-5 text-text-secondary">{step.description}</p>
						</article>
					))}
				</div>

				<div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
					<section className="rounded-md border border-surface-border bg-surface-background px-3 py-3">
						<div className="flex items-center gap-2">
							<LineChart className="h-4 w-4 text-brand-primary" aria-hidden="true" />
							<h3 className="text-sm font-semibold text-text-primary">当前可追溯对象</h3>
						</div>
						{runs.isLoading ? (
							<p className="mt-3 text-sm text-text-muted">加载评测运行版本链…</p>
						) : runs.isError ? (
							<p className="mt-3 text-sm text-brand-danger">版本责任链加载失败</p>
						) : currentRun ? (
							<dl className="mt-3 grid gap-3 sm:grid-cols-2">
								<VersionDetail label="评测运行" value={currentRun.run_name ?? "未命名评测"} />
								<VersionDetail label="数据集版本" value={currentRun.dataset_version} />
								<VersionDetail
									label="对比快照"
									value={currentRun.baseline_run_id ? "可保存 baseline/current 快照" : "独立运行"}
								/>
								<VersionDetail
									label="自动版本归因"
									value={
										auditRefs.length > 0 ? `已挂 ${auditRefs.length} 条资产审计` : "暂无匹配审计"
									}
								/>
							</dl>
						) : (
							<p className="mt-3 text-sm text-text-muted">
								暂无 current 运行，先完成一次修复后评测。
							</p>
						)}
					</section>

					<section className="rounded-md border border-surface-border bg-surface-background px-3 py-3">
						<h3 className="text-sm font-semibold text-text-primary">资产版本</h3>
						{assetEntries.length > 0 ? (
							<div className="mt-3 space-y-2">
								{assetEntries.map(([key, value]) => (
									<div
										key={key}
										className="flex flex-col gap-1 rounded-md bg-surface-card px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
									>
										<span className="text-xs font-medium text-text-muted">{assetLabel(key)}</span>
										<span className="break-words text-xs font-semibold text-text-primary">
											{String(value)}
										</span>
									</div>
								))}
							</div>
						) : (
							<p className="mt-3 text-sm text-text-muted">当前运行还没有资产版本快照。</p>
						)}
					</section>
				</div>

				<div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
					<section className="rounded-md border border-surface-border bg-surface-background px-3 py-3">
						<h3 className="text-sm font-semibold text-text-primary">自动版本归因</h3>
						{auditRefs.length > 0 ? (
							<div className="mt-3 space-y-2">
								{auditRefs.map((item) => (
									<AuditRefRow key={item.audit_id} item={item} />
								))}
							</div>
						) : (
							<p className="mt-3 text-sm text-text-muted">当前运行还没有匹配到资产变更审计。</p>
						)}
					</section>

					<section className="rounded-md border border-surface-border bg-surface-background px-3 py-3">
						<div className="flex items-center justify-between gap-3">
							<h3 className="text-sm font-semibold text-text-primary">统一资产审计</h3>
							<span className="rounded border border-surface-border bg-surface-card px-2 py-1 text-xs font-medium text-text-secondary">
								审计记录 {auditItems.length} 条
							</span>
						</div>
						{audits.isLoading ? (
							<p className="mt-3 text-sm text-text-muted">加载资产审计…</p>
						) : audits.isError ? (
							<p className="mt-3 text-sm text-brand-danger">资产审计加载失败</p>
						) : auditItems.length > 0 ? (
							<div className="mt-3 space-y-2">
								{auditItems.slice(0, 3).map((item) => (
									<AuditItemRow key={item.audit_id} item={item} />
								))}
							</div>
						) : (
							<p className="mt-3 text-sm text-text-muted">暂无资产变更审计记录。</p>
						)}
					</section>
				</div>

				<div className="flex flex-col gap-2 rounded-md border border-brand-success/30 bg-brand-success/10 px-3 py-3 md:flex-row md:items-center md:justify-between">
					<p className="text-sm leading-6 text-brand-success">
						后端统一审计日志已接入版本责任链；后续会继续把更多资产编辑入口自动写入审计。
					</p>
					<PanelLink to="/prompt-q" hash="prompt-asset-change-audit" label="去资产审计入口" />
				</div>
			</CardContent>
		</Card>
	);
}

export function AbExperimentGovernancePanel() {
	return (
		<Card data-testid="m07-ab-experiment-governance">
			<CardHeader className="space-y-2">
				<div className="flex items-start gap-3">
					<span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-surface-subtle text-brand-primary">
						<FlaskConical className="h-4 w-4" aria-hidden="true" />
					</span>
					<div>
						<div className="flex flex-wrap items-center gap-2">
							<CardTitle className="text-lg">实验治理前置条件</CardTitle>
							<span className="rounded border border-brand-warning/30 bg-brand-warning/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand-warning">
								暂不启动线上分流
							</span>
						</div>
						<CardDescription className="mt-1 leading-6">
							先做离线对比、准入检查和责任链说明，再决定是否进入线上候选和审核队列。
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="grid gap-3 md:grid-cols-4">
					{EXPERIMENT_STEPS.map((step, index) => (
						<article
							key={step}
							className="rounded-md border border-surface-border bg-surface-background px-3 py-3"
						>
							<span className="text-xs font-semibold text-brand-primary">步骤 {index + 1}</span>
							<p className="mt-2 text-sm leading-6 text-text-primary">{step}</p>
						</article>
					))}
				</div>
				<div className="overflow-x-auto">
					<table className="w-full min-w-[880px] border-collapse text-sm">
						<thead className="bg-surface-subtle text-left text-xs text-text-muted">
							<tr>
								<th className="px-3 py-2 font-medium">前置项</th>
								<th className="px-3 py-2 font-medium">当前状态</th>
								<th className="px-3 py-2 font-medium">已有证据</th>
								<th className="px-3 py-2 font-medium">下一步</th>
							</tr>
						</thead>
						<tbody>
							{EXPERIMENT_READINESS.map((item) => (
								<tr key={item.item} className="border-t border-surface-border align-top">
									<td className="px-3 py-3 font-semibold text-text-primary">{item.item}</td>
									<td className="px-3 py-3 text-text-secondary">{item.status}</td>
									<td className="px-3 py-3 text-text-secondary">{item.evidence}</td>
									<td className="px-3 py-3 text-text-secondary">{item.next}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<section className="rounded-md border border-surface-border bg-surface-background px-3 py-3">
					<div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<h3 className="text-sm font-semibold text-text-primary">实验配置</h3>
							<p className="mt-1 text-xs leading-5 text-text-secondary">
								先固定一次实验必须填写的业务口径；本阶段仍不做真实流量。
							</p>
						</div>
						<span className="w-fit rounded border border-surface-border bg-surface-card px-2 py-1 text-xs font-medium text-text-secondary">
							合同先行
						</span>
					</div>
					<div className="mt-3 overflow-x-auto">
						<table className="w-full min-w-[980px] border-collapse text-sm">
							<thead className="bg-surface-subtle text-left text-xs text-text-muted">
								<tr>
									<th className="px-3 py-2 font-medium">配置项</th>
									<th className="px-3 py-2 font-medium">必填口径</th>
									<th className="px-3 py-2 font-medium">当前承接</th>
									<th className="px-3 py-2 font-medium">未满足时处理</th>
								</tr>
							</thead>
							<tbody>
								{EXPERIMENT_CONFIG_CONTRACT.map((item) => (
									<tr key={item.item} className="border-t border-surface-border align-top">
										<td className="px-3 py-3 font-semibold text-text-primary">{item.item}</td>
										<td className="px-3 py-3 text-text-secondary">{item.required}</td>
										<td className="px-3 py-3 text-text-secondary">{item.current}</td>
										<td className="px-3 py-3 text-text-secondary">{item.fallback}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</section>
				<section className="rounded-md border border-surface-border bg-surface-background px-3 py-3">
					<div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<h3 className="text-sm font-semibold text-text-primary">实验结论快照</h3>
							<p className="mt-1 text-xs leading-5 text-text-secondary">
								把一次离线实验结论沉淀成可追溯、可对比、可审核、可验收的复盘对象。
							</p>
						</div>
						<span className="w-fit rounded border border-surface-border bg-surface-card px-2 py-1 text-xs font-medium text-text-secondary">
							快照先行
						</span>
					</div>
					<div className="mt-3 grid gap-3 md:grid-cols-4">
						{EXPERIMENT_SNAPSHOT_STANDARDS.map((item) => (
							<article key={item.title} className="rounded-md bg-surface-card px-3 py-3">
								<p className="text-sm font-semibold text-text-primary">{item.title}</p>
								<p className="mt-2 text-xs leading-5 text-text-secondary">{item.description}</p>
							</article>
						))}
					</div>
					<div className="mt-3 overflow-x-auto">
						<table className="w-full min-w-[900px] border-collapse text-sm">
							<thead className="bg-surface-subtle text-left text-xs text-text-muted">
								<tr>
									<th className="px-3 py-2 font-medium">快照字段</th>
									<th className="px-3 py-2 font-medium">事实源</th>
									<th className="px-3 py-2 font-medium">验收含义</th>
								</tr>
							</thead>
							<tbody>
								{EXPERIMENT_SNAPSHOT_FIELDS.map((item) => (
									<tr key={item.item} className="border-t border-surface-border align-top">
										<td className="px-3 py-3 font-semibold text-text-primary">{item.item}</td>
										<td className="px-3 py-3 text-text-secondary">{item.source}</td>
										<td className="px-3 py-3 text-text-secondary">{item.meaning}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</section>
				<div className="flex flex-col gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-3 md:flex-row md:items-center md:justify-between">
					<p className="text-sm leading-6 text-brand-warning">
						<span className="font-semibold">当前结论：</span>
						先做离线对比和准入检查，当前不代表已经启动线上流量实验；影响回复的提示词、方案、知识和工具改动仍要先经过资产审计与评测运行。
					</p>
					<div className="flex flex-col gap-2 sm:flex-row">
						<PanelLink to="/prompt-q" hash="prompt-asset-change-audit" label="去提示词与话术" />
						<PanelLink to="/test-datasets" label="去样本库" />
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function PanelLink({
	to,
	hash,
	label,
}: {
	to: "/test-datasets" | "/prompt-q";
	hash?: string;
	label: string;
}) {
	return (
		<Link
			to={to}
			hash={hash}
			className="inline-flex items-center justify-center gap-1.5 rounded-md border border-surface-border bg-surface-card px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-subtle"
		>
			{label}
			<ArrowRight className="h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
		</Link>
	);
}

function VersionDetail({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<dt className="text-xs font-medium text-text-muted">{label}</dt>
			<dd className="mt-1 break-words text-sm font-semibold text-text-primary">{value}</dd>
		</div>
	);
}

type AuditRef = {
	audit_id: string;
	asset_type: string;
	asset_id: string;
	asset_version?: string | null;
	change_summary: string;
	created_at: string;
};

function AuditRefRow({ item }: { item: AuditRef }) {
	return (
		<div className="rounded-md bg-surface-card px-3 py-2">
			<div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
				<span className="text-xs font-semibold text-text-primary">{item.audit_id}</span>
				<span className="text-xs text-text-muted">
					{assetLabel(item.asset_type)} / {item.asset_id}
				</span>
			</div>
			<p className="mt-1 text-xs leading-5 text-text-secondary">{item.change_summary}</p>
		</div>
	);
}

function AuditItemRow({ item }: { item: AssetChangeAuditItem }) {
	return (
		<div className="rounded-md bg-surface-card px-3 py-2">
			<div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
				<span className="text-xs font-semibold text-text-primary">
					{assetLabel(item.asset_type)} / {item.asset_id}
				</span>
				<span className="text-xs text-text-muted">{item.change_type}</span>
			</div>
			<p className="mt-1 text-xs leading-5 text-text-secondary">{item.change_summary}</p>
		</div>
	);
}

function findCurrentRun(items: EvalRunItem[]) {
	return items.find((run) => run.comparison_role === "current") ?? items[0] ?? null;
}

function getAuditRefs(run: EvalRunItem | null): AuditRef[] {
	const raw = run?.asset_versions?.asset_change_audit_refs;
	if (!Array.isArray(raw)) return [];
	return raw
		.filter((item): item is Record<string, unknown> => item !== null && typeof item === "object")
		.map((item) => ({
			audit_id: String(item.audit_id ?? ""),
			asset_type: String(item.asset_type ?? "unknown"),
			asset_id: String(item.asset_id ?? "unknown"),
			asset_version: item.asset_version === undefined ? null : String(item.asset_version),
			change_summary: String(item.change_summary ?? "未填写变更摘要"),
			created_at: String(item.created_at ?? ""),
		}))
		.filter((item) => item.audit_id);
}

function assetLabel(key: string) {
	if (key === "intent_rules") return "意图规则";
	if (key === "intent_rule") return "意图规则";
	if (key === "intent_rule_version") return "意图规则";
	if (key === "playbook_mapping") return "处理方案映射";
	if (key === "playbook_mapping_version") return "处理方案映射";
	if (key === "playbook_version") return "处理方案";
	if (key === "agent_prompt") return "主回复提示词";
	if (key === "agent_prompt_version") return "主回复提示词";
	if (key === "knowledge_snapshot") return "知识证据";
	if (key === "knowledge_snapshot_version") return "知识证据";
	if (key === "skill_registry") return "工具动作";
	if (key === "skill_registry_version") return "工具动作";
	if (key === "quality_gate") return "质量策略";
	if (key === "dataset") return "数据集";
	if (key === "dataset_id") return "数据集";
	if (key === "dataset_version") return "数据集版本";
	if (key === "code_version") return "代码版本";
	return key;
}
